import {
    ContactDetails,
    ContactDetailsChange,
    getChangeContactDetailsEndpoint,
    RequestStatus,
    editViewContactSectionValidationSchema,
} from 'cpfr-common';
import {
    NotificationStatus,
    preventSubmit,
    Spinner,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    useAnalyticsPageViewTracker,
    ValidatedInput,
} from '@cp-shared-19/frontend-ui';
import {
    Button,
    ButtonContainer,
    DataOverview,
    Fieldset,
    Form,
    Layout,
    Notification,
} from '@vwfs-bronson/bronson-react';
import { updateContactInformationChangeStatus } from 'components/my-profile/ContactInformationChangeStatusSlice';
import { PhoneInput } from 'components/request/form-view/category-selection/category-view/simple-form-components/phone-input/PhoneInput';
import { CpDataApi } from 'cp-xhr';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getAreaCode, removeAreaCodeFromPhoneNumber } from './helper';
import { useFocusOnMount } from '../../../../utils/useFocusOnMount';
import { useSubmitOnce } from '../../../../utils/useSubmitOnce';
import { getErrorCode } from '../../../../utils/getErrorCode';
import { isDuplicateSubmissionError, isPendingRequestError } from '../../../../utils/duplicateSubmission';

export enum EditStatus {
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    NO_CHANGE_DETECTED = 'NO_CHANGE_DETECTED',
    NOT_PERFORMED = 'NOT_PERFORMED',
    ALREADY_IN_PROGRESS = 'ALREADY_IN_PROGRESS',
}

export type EditViewProps = {
    contactDetails?: ContactDetails;
    cancelEditing: () => void;
    finishEditing: (newEditStatus: EditStatus, updatedContactDetails?: ContactDetails) => void;
};

export const EditView: React.FC<EditViewProps> = ({ contactDetails, cancelEditing, finishEditing }) => {
    const { isSubmitting, run } = useSubmitOnce();
    const [isError, setIsError] = useState(false);
    const [noChangeError, setNoChangeError] = useState(false);
    const dispatch = useDispatch();

    const trackingSection = 'Contact';
    useAnalyticsPageViewTracker('editProfileSectionDisplayed', true, trackingSection);
    const { onAction: onValidationError } = useAnalyticsActionTracker('onEditProfileContactValidationError');
    const { onTyping } = useAnalyticsFormTracker({
        startTyping: 'onEditProfileContactTypedIn',
    });
    const getInitialErrors = (values: { [k: string]: string | undefined }) =>
        ['emailAddress', 'mobilePhone'].filter((element) => !values[element] || values[element] === '').join(', ');
    const getErrors = (errors: { [k: string]: string | undefined }) => Object.keys(errors).join(`, `);

    const { t } = useTranslation(['my-profile', 'loader']);
    const validationPrefix = 'contact-details.edit-view.validation';

    useFocusOnMount('input[name=emailAddress]', { preventScroll: true });

    const initialValues: ContactDetailsChange = {
        emailAddress: contactDetails?.email || '',
        mobilePhoneCountryCode: getAreaCode(contactDetails?.mobilePhone) || '33',
        mobilePhone: removeAreaCodeFromPhoneNumber(contactDetails?.mobilePhone),
        otherPhoneCountryCode: getAreaCode(contactDetails?.otherPhone) || '33',
        otherPhone: removeAreaCodeFromPhoneNumber(contactDetails?.otherPhone),
    };

    const handleSubmit = ({
        emailAddress,
        mobilePhoneCountryCode,
        mobilePhone,
        otherPhoneCountryCode,
        otherPhone,
    }: ContactDetailsChange): Promise<void> =>
        run(async () => {
            const preparedChangeValues: ContactDetailsChange = {
                emailAddress: emailAddress,
                mobilePhoneCountryCode: `+${mobilePhoneCountryCode}`,
                mobilePhone: mobilePhone.replace(/\s/g, ''),
                otherPhoneCountryCode: otherPhoneCountryCode ? `+${otherPhoneCountryCode}` : '',
                otherPhone: otherPhone?.replace(/\s/g, ''),
            };

            setNoChangeError(false);
            setIsError(false);

            try {
                await CpDataApi.put(getChangeContactDetailsEndpoint(), preparedChangeValues);
            } catch (error) {
                // An earlier change of this type is still open at the market, so this
                // one was not applied. Saying "success" here would hide that.
                if (isPendingRequestError(error)) {
                    finishEditing(EditStatus.ALREADY_IN_PROGRESS);
                    return;
                }
                // A duplicate is not a failure: the change is already registered,
                // so fall through to the success path instead of offering a retry.
                if (!isDuplicateSubmissionError(error)) {
                    if (getErrorCode(error) === 'NO_CHANGE_DETECTED') setNoChangeError(true);
                    else {
                        setIsError(true);
                    }
                    return;
                }
            }

            const preparedChanges: ContactDetails = {
                email: emailAddress,
                mobilePhone: `+${mobilePhoneCountryCode} ${mobilePhone.replace(/\s/g, '')}`,
                otherPhone: otherPhone ? `+${otherPhoneCountryCode} ${otherPhone?.replace(/\s/g, '')}` : '',
            };

            finishEditing(EditStatus.SUCCESS, { ...contactDetails, ...preparedChanges });
            dispatch(
                updateContactInformationChangeStatus({
                    requestCustomerStatus: RequestStatus.IN_PROGRESS,
                }),
            );
        });

    const closeButtonProps = {
        onClick: (event: React.MouseEvent) => {
            event.preventDefault();
            cancelEditing();
        },
        className: 'c-icon  c-icon--[semantic-close]',
        testId: 'x-button',
    };

    const errorMessages = {
        emailAddress: {
            required: t(`${validationPrefix}.email.required`),
            email: t(`${validationPrefix}.email.invalid`),
        },
        mobilePhone: {
            required: t(`${validationPrefix}.mobile-phone.required`),
            matches: t(`${validationPrefix}.phone.invalid`),
        },
        otherPhone: {
            required: t(`${validationPrefix}.other-phone.required`),
            matches: t(`${validationPrefix}.phone.invalid-other`),
        },
        mobilePhoneCountryCode: {
            required: 'Mobile phone country code is required',
        },
        otherPhoneCountryCode: {},
    };

    return (
        <DataOverview
            href=""
            titleLevel={2}
            title={t('contact-details.edit-view.headline')}
            buttonLabel={' '}
            buttonProps={closeButtonProps}
            id="contact-section-edit-view-id"
        >
            <>
                <Notification
                    status={NotificationStatus.warning}
                    className={'u-mb'}
                    title={''}
                    hideTitle={true}
                    visible
                    showCloseButton={false}
                >
                    {t('contact-details.edit-view.info')}
                </Notification>
                {isSubmitting && (
                    <Spinner center>
                        <p>{t('genericText', { ns: 'loader' })}</p>
                    </Spinner>
                )}
                <Formik
                    initialValues={initialValues}
                    validationSchema={editViewContactSectionValidationSchema(errorMessages)}
                    onSubmit={handleSubmit}
                >
                    {(formik) => (
                        <Form
                            onSubmit={(e) => preventSubmit(e)}
                            data-testid="edit-form"
                            onChange={() => onTyping(formik.errors, formik.touched)}
                        >
                            <Fieldset>
                                <Fieldset.Row>
                                    <Layout>
                                        <Layout.Item>
                                            <ValidatedInput
                                                className="c-form-field--no-floating"
                                                label={t('contact-details.edit-view.email')}
                                                name="emailAddress"
                                                testId="email-address"
                                                inputId="email-address-my-prifile-contract-section"
                                                type="email"
                                                aria-required={true}
                                                isMandatory={true}
                                                requiredText=" *"
                                                autocomplete="email"
                                            />
                                        </Layout.Item>
                                        <Layout.Item default="1/2" s="1/1">
                                            <PhoneInput
                                                phoneFieldLabel={t('contact-details.edit-view.mobile-phone-digits')}
                                                phoneFieldName={'mobilePhone'}
                                                phoneFieldTestId={'mobile-phone'}
                                                phoneFieldPrefixLabel={t(
                                                    'contact-details.edit-view.mobile-phone-country-code',
                                                )}
                                                phoneFieldPrefixName={'mobilePhoneCountryCode'}
                                                setFormikFieldValue={formik.setFieldValue}
                                                inputId="mobile-phone-my-prifile-contract-section"
                                                isMandatory={true}
                                                hidePrefix={false}
                                            />
                                        </Layout.Item>
                                        <Layout.Item default="1/2" s="1/1">
                                            <PhoneInput
                                                phoneFieldLabel={t('contact-details.edit-view.fixed-phone-digits')}
                                                phoneFieldName={'otherPhone'}
                                                phoneFieldTestId={'main-phone'}
                                                phoneFieldPrefixLabel={t(
                                                    'contact-details.edit-view.mobile-phone-country-code',
                                                )}
                                                phoneFieldPrefixName={'otherPhoneCountryCode'}
                                                setFormikFieldValue={formik.setFieldValue}
                                                padding
                                                inputId="main-phone-my-prifile-contract-section"
                                            />
                                        </Layout.Item>
                                    </Layout>
                                </Fieldset.Row>
                            </Fieldset>
                            {isError && (
                                <Notification
                                    status={NotificationStatus.error}
                                    testId={'errorNotification'}
                                    className="u-mb"
                                    title={''}
                                    hideTitle={true}
                                    visible
                                    showCloseButton={false}
                                >
                                    {t('contact-details.notification.error')}
                                </Notification>
                            )}
                            {noChangeError && (
                                <Notification
                                    status={NotificationStatus.error}
                                    testId={'errorNotification'}
                                    className="u-mb"
                                    title={''}
                                    hideTitle={true}
                                    visible
                                    showCloseButton={false}
                                >
                                    {t('contact-details.notification.no-change')}
                                </Notification>
                            )}
                            <Fieldset>
                                <Fieldset.Row>
                                    <ButtonContainer center>
                                        <Button
                                            secondary
                                            onClick={() => {
                                                cancelEditing();
                                            }}
                                            testId="cancel-button"
                                            type="button"
                                            disabled={isSubmitting}
                                        >
                                            {t('contact-details.edit-view.back-button')}
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                formik.handleSubmit();
                                                const initialErrors = getInitialErrors({
                                                    emailAddress: formik.values.emailAddress,
                                                    mobilePhone: formik.values.mobilePhone,
                                                });
                                                if (!isEmpty(formik.errors)) {
                                                    const errorsList = getErrors(formik.errors);
                                                    onValidationError(errorsList);
                                                } else if (!!initialErrors) {
                                                    onValidationError(initialErrors);
                                                }
                                            }}
                                            testId="submit-button"
                                            type="button"
                                            disabled={isSubmitting}
                                        >
                                            {t('contact-details.edit-view.confirm-button')}
                                        </Button>
                                    </ButtonContainer>
                                </Fieldset.Row>
                            </Fieldset>
                        </Form>
                    )}
                </Formik>
            </>
        </DataOverview>
    );
};
